import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import React from 'react';
import { KeyUtils } from 'slate';
import Delta from 'quill-delta';
import { CollabDocumentContext } from '../components/CollabDocumentProvider';
import genKey from '../utils/genKey';
import { init } from '../adapter/editor';
import PerfAnalyticsHelper from '../helpers/PerfAnalyticsHelper';

var isOpsEmpty = function isOpsEmpty(ops) {
  return new Delta(ops).chop().length() === 0;
};

export default function provideEditorValue() {
  var contextType = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : CollabDocumentContext;
  return function (WrappedComponent) {
    return function ComponentWithEditorValue(_ref) {
      var onEditorRef = _ref.onEditorRef,
          readOnly = _ref.readOnly,
          props = _objectWithoutProperties(_ref, ["onEditorRef", "readOnly"]);

      var _useSyncEditorValue = useSyncEditorValue({
        contextType: contextType,
        onEditorRef: onEditorRef
      }),
          _useSyncEditorValue2 = _slicedToArray(_useSyncEditorValue, 4),
          value = _useSyncEditorValue2[0],
          onChange = _useSyncEditorValue2[1],
          onError = _useSyncEditorValue2[2],
          onEditorRefCallback = _useSyncEditorValue2[3];

      return React.createElement(WrappedComponent, Object.assign({}, props, {
        value: value,
        onChange: onChange,
        onError: onError,
        readOnly: readOnly,
        onEditorRef: onEditorRefCallback
      }));
    };
  };
}

function useSyncEditorValue(_ref2) {
  var contextType = _ref2.contextType,
      onEditorRef = _ref2.onEditorRef;

  // @ts-ignore
  var _React$useContext = React.useContext(contextType),
      doc = _React$useContext.doc,
      onError = _React$useContext.onError;

  var editor = React.useRef();

  var _React$useState = React.useState(),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      value = _React$useState2[0],
      setValue = _React$useState2[1];

  var onRemoteChange = React.useCallback(function (ops, isLocal) {
    if (isLocal) return;
    var delta = new Delta(ops);

    if (!isOpsEmpty(delta.ops)) {
      try {
        editor.current.applyRemoteChanges(delta);
      } catch (error) {
        onError(error);
      }
    }
  }, [editor, onError]);
  var onInit = React.useCallback(function () {
    PerfAnalyticsHelper.logServerTime();
    doc.on('op', onRemoteChange);
    setValue(init(doc.data));
  }, [doc, onRemoteChange]);
  var onLocalChange = React.useCallback(function () {
    var _editor$current = editor.current,
        value = _editor$current.value,
        readOnly = _editor$current.readOnly; // This need to be done before anything else, or any updates from the
    // context or the hooks will set a deprecated value on the editor
    // component

    setValue(value);

    if (!readOnly) {
      var delta = editor.current.getLocalChanges();

      if (!isOpsEmpty(delta.ops)) {
        doc.submitOp(delta.ops);
      }
    }
  }, [doc]);
  React.useEffect(function () {
    KeyUtils.resetGenerator();
    KeyUtils.setGenerator(genKey);
    PerfAnalyticsHelper.initLogs();

    if (doc.data) {
      onInit();
    } else {
      setValue(undefined);
      doc.once('load', onInit);
    }

    return function () {
      doc.removeListener('load', onInit);
      doc.removeListener('op', onRemoteChange);
    };
  }, [doc, onInit, onRemoteChange]);
  var onEditorRefCallback = React.useCallback(function (editorRef) {
    editor.current = editorRef;

    if (onEditorRef) {
      onEditorRef(editorRef);
    }
  }, [onEditorRef]);
  return [value, onLocalChange, onError, onEditorRefCallback];
}